import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Loader2,
  Quote,
  Redo,
  Save,
  Strikethrough,
  Undo,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { useGetFirmQuery, useUpdateFirmMutation } from '@/services/api/firmsService';

// ---------------------------------------------------------------------------
// Toolbar Button
// ---------------------------------------------------------------------------

const ToolbarBtn: React.FC<{
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
}> = ({ onClick, active, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'p-1.5 rounded-md transition-colors',
      active ? 'bg-zinc-200 text-zinc-900' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700',
      disabled && 'opacity-30 cursor-not-allowed',
    )}
  >
    {children}
  </button>
);

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Toolbar: React.FC<{ editor: any }> = ({ editor }) => {
  if (!editor) return null;

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-zinc-200 bg-zinc-50 rounded-t-xl flex-wrap">
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
        <Bold className="size-4" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
        <Italic className="size-4" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
        <Strikethrough className="size-4" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline Code">
        <Code className="size-4" />
      </ToolbarBtn>

      <div className="w-px h-5 bg-zinc-200 mx-1" />

      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
        <Heading1 className="size-4" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
        <Heading2 className="size-4" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
        <Heading3 className="size-4" />
      </ToolbarBtn>

      <div className="w-px h-5 bg-zinc-200 mx-1" />

      <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
        <List className="size-4" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List">
        <ListOrdered className="size-4" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote">
        <Quote className="size-4" />
      </ToolbarBtn>

      <div className="w-px h-5 bg-zinc-200 mx-1" />

      <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <Undo className="size-4" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <Redo className="size-4" />
      </ToolbarBtn>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const FirmSopPage: React.FC = () => {
  const { firm_id } = useParams<{ firm_id: string }>();
  const { data: firm, isLoading } = useGetFirmQuery(firm_id ?? '', { skip: !firm_id });
  const [updateFirm, { isLoading: isSaving }] = useUpdateFirmMutation();
  const [isDirty, setIsDirty] = useState(false);
  const justSavedRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Start writing your Standard Operating Procedures here...',
      }),
    ],
    content: firm?.sop || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none p-4 min-h-[500px] focus:outline-none',
      },
    },
    onUpdate: () => {
      setIsDirty(true);
    },
  });

  // Load SOP content when firm data arrives (skip right after save to avoid flicker)
  // Load SOP content when firm data arrives (skip if editor content matches or user is editing)
  const lastSavedContentRef = useRef<string>('');
  useEffect(() => {
    if (editor && firm?.sop !== undefined && !isDirty) {
      const serverContent = firm.sop || '';
      if (serverContent !== lastSavedContentRef.current) {
        editor.commands.setContent(serverContent);
        lastSavedContentRef.current = serverContent;
      }
    }
  }, [editor, firm?.sop, isDirty]);

  const handleSave = useCallback(async () => {
    if (!firm_id || !editor) return;
    const html = editor.getHTML();
    try {
      await updateFirm({ id: firm_id, sop: html }).unwrap();
      justSavedRef.current = true;
      setIsDirty(false);
      toast.success('SOP saved');
    } catch {
      toast.error('Failed to save SOP');
    }
  }, [firm_id, editor, updateFirm]);

  // Cmd+S to save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty) handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isDirty, handleSave]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="size-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-zinc-900">Standard Operating Procedures</h2>
          <p className="text-sm font-medium text-zinc-500 mt-0.5">
            Document firm-specific workflows, guidelines, and processes.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all',
            isDirty
              ? 'bg-zinc-900 text-white hover:bg-zinc-800'
              : 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
          )}
        >
          {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Editor */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <Toolbar editor={editor} />
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};
